import { createSelector } from 'reselect';
import * as libSelectors from 'store/libs/selectors';
import brand from 'resources/brand';

import {
  selectIsPollingAnyAdvisory,
  selectUserAdvisories,
} from 'store/features/users/advisories/selectors';
import {
  selectIsPollingAnyTeamMembership,
  selectUserTeamMemberships,
} from 'store/features/users/team-memberships/selectors';

import { ADVISOR_CONNECTION_STATUSES } from 'store/features/company-advisor-connections/helpers';
import { TEAM_MEMBER_CONNECTION_STATUSES } from 'store/features/company-team-member-connections/helpers';

const selectAuthFeatureState = state => state.features.auth;

export const selectIsSignedUp = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.signedUp
);

export const selectEmailVerification = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.emailVerification
);

export const selectAuthToken = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.token
);

export const selectIsLoggedIn = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.loggedIn
);

export const selectCurrentUser = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.currentUser
);

export const selectIsFetchingCurrentUser = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.fetchingCurrentUser
);

export const selectCurrentUserFetchedAtleastOnce = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.currentUserFetchedAtleastOnce
);

export const selectCurrentUserInitialized = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.currentUserInitialized
);

export const selectIsCurrentUserApproved = createSelector(
  selectCurrentUser,
  currentUser =>
    currentUser && currentUser.approved ? currentUser.approved.toString() === 'true' : false
);

export const selectIsCurrentUserAdmin = createSelector(
  selectCurrentUser,
  currentUser => (currentUser ? currentUser.role === 'admin' : false)
);

export const selectIsCurrentUserFullAccess = createSelector(
  selectCurrentUser,
  currentUser => (currentUser ? currentUser.role === 'full_access' : false)
);

// validator.group
export const selectIsCurrentUserTier3 = createSelector(
  selectCurrentUser,
  currentUser =>
    currentUser && currentUser.validator && currentUser.validator.group === '_3' ? true : false
);

export const selectIsCurrentUserNoAccess = createSelector(
  selectCurrentUser,
  currentUser => (currentUser ? !currentUser.role || currentUser.role === 'no_access' : false)
);

export const selectCurrentUserId = createSelector(
  selectAuthFeatureState,
  authFeatureState => (authFeatureState.currentUser ? authFeatureState.currentUser.id : undefined)
);

export const selectCurrentUsername = createSelector(
  selectAuthFeatureState,
  authFeatureState =>
    authFeatureState.currentUser ? authFeatureState.currentUser.username : undefined
);

export const selectCurrentUserEmail = createSelector(
  selectAuthFeatureState,
  authFeatureState =>
    authFeatureState.currentUser ? authFeatureState.currentUser.email : undefined
);

export const selectCurrentUserHasAutoGeneratedUsername = createSelector(
  selectAuthFeatureState,
  authFeatureState =>
    authFeatureState.currentUser ? authFeatureState.currentUser.isAutoGeneratedUsername : undefined
);

export const selectCurrentUserEthereumAddress = createSelector(
  selectAuthFeatureState,
  authFeatureState =>
    authFeatureState.currentUser ? authFeatureState.currentUser.ethereumAddress : undefined
);

export const selectCurrentUserRole = createSelector(
  selectAuthFeatureState,
  authFeatureState => (authFeatureState.currentUser ? authFeatureState.currentUser.role : undefined)
);

export const selectCurrentUserHasPassword = createSelector(
  selectAuthFeatureState,
  authFeatureState =>
    authFeatureState.currentUser ? authFeatureState.currentUser.isPass : undefined
);

export const selectIsForgotPasswordEmailSent = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.forgotPasswordEmailSent
);

export const selectIsFacebookAuthAvailable = createSelector(
  libSelectors.selectFacebookSdkLibLoadState,
  facebookSdkLibLoadState =>
    facebookSdkLibLoadState &&
    facebookSdkLibLoadState.loaded &&
    process.env.REACT_APP_FACEBOOK_AUTH_ENABLED === 'true'
);

export const selectIsAuthenticatingWithFacebook = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.authenticatingWithFacebook
);

export const selectIsLinkingFacebook = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.linkingWithFacebook
);

export const selectHasCurrentUserLinkedFacebook = createSelector(
  selectCurrentUser,
  currentUser => currentUser && !!currentUser.isFacebookLinked
);

export const selectIsGoogleAuthAvailable = createSelector(
  libSelectors.selectGoogleApiLibLoadState,
  libSelectors.selectGoogleAuthLibLoadState,
  libSelectors.selectGoogleAuthLibLoadState,
  (googleApiLibLoadState, googleAuthLibLoadState) =>
    googleApiLibLoadState &&
    googleApiLibLoadState.loaded &&
    googleAuthLibLoadState &&
    googleAuthLibLoadState.loaded
);

export const selectIsAuthenticatingWithGoogle = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.authenticatingWithGoogle
);

export const selectIsLinkingGoogle = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.linkingWithGoogle
);

export const selectHasCurrentUserLinkedGoogle = createSelector(
  selectCurrentUser,
  currentUser => currentUser && !!currentUser.isGoogleLinked
);

export const selectIsLinkedInAuthAvailable = createSelector(() => true);

export const selectIsAuthenticatingWithLinkedIn = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.authenticatingWithLinkedIn
);

export const selectIsLinkingLinkedIn = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.linkingWithLinkedIn
);

export const selectHasCurrentUserLinkedLinkedIn = createSelector(
  selectCurrentUser,
  currentUser => currentUser && !!currentUser.isLinkedInLinked
);

/* Start CIVIC auth selectors */
export const selectIsCivicAuthAvailable = createSelector(
  libSelectors.selectCivicSdkLibLoadState,
  civicSdkLibLoadState => civicSdkLibLoadState && civicSdkLibLoadState.loaded
);

export const selectIsAuthenticatingWithCivic = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.authenticatingWithCivic
);

export const selectIsLinkingCivic = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.linkingWithCivic
);

export const selectHasCurrentUserLinkedCivic = createSelector(
  selectCurrentUser,
  currentUser => !!currentUser.isCivicLinked
);
/* End CIVIC auth selectors */

export const selectIsAirbitzAuthAvailable = createSelector(
  libSelectors.selectAirbitzUIContextLoadState,
  airbitzUIContextLoadState => airbitzUIContextLoadState.loaded
);

export const selectIsAuthenticatingWithAirbitz = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.authenticatingWithAirbitz
);

export const selectIsLinkingAirbitz = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.linkingWithAirbitz
);

export const selectHasCurrentUserLinkedAirbitz = createSelector(
  selectCurrentUser,
  currentUser => currentUser && !!currentUser.isAirbitzLinked
);

export const selectHasCurrentUserLinkedGithub = createSelector(
  selectCurrentUser,
  currentUser => currentUser && currentUser.githubUid && !!currentUser.githubUid
);

export const selectIsAuthenticatingWithExternalAccount = createSelector(
  selectIsAuthenticatingWithFacebook,
  selectIsAuthenticatingWithGoogle,
  selectIsAuthenticatingWithLinkedIn,
  selectIsAuthenticatingWithAirbitz,
  selectIsAuthenticatingWithCivic,
  (
    authenticatingWithFacebook,
    authenticatingWithGoogle,
    authenticatingWithLinkedIn,
    authenticatingWithAirbitz,
    authenticatingWithCivic
  ) =>
    authenticatingWithFacebook ||
    authenticatingWithGoogle ||
    authenticatingWithLinkedIn ||
    authenticatingWithAirbitz ||
    authenticatingWithCivic
);

export const selectIsLinkingWithExternalAccount = createSelector(
  selectIsLinkingFacebook,
  selectIsLinkingGoogle,
  selectIsLinkingLinkedIn,
  selectIsLinkingAirbitz,
  (linkingWithFacebook, linkingWithGoogle, linkingWithLinkedIn, linkingWithAirbitz) =>
    linkingWithFacebook || linkingWithGoogle || linkingWithLinkedIn || linkingWithAirbitz
);

export const selectCurrentUserImportedLinkedInPdfAtleastOnce = createSelector(
  selectCurrentUser,
  currentUser =>
    currentUser &&
    currentUser.lastLinkedinPdfImportAt &&
    currentUser.lastLinkedinPdfImportAt.length &&
    currentUser.lastLinkedinPdfImportAt[0]
);

export const selectIsSavingCurrentUser = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.saving
);

export const selectCurrentUserHasConnectionsEntity = createSelector(
  selectCurrentUser,
  currentUser => currentUser && currentUser.hasConnectionEntity
);

export const selectCountsOfLinkedAccounts = createSelector(
  selectHasCurrentUserLinkedFacebook,
  selectHasCurrentUserLinkedGoogle,
  selectHasCurrentUserLinkedLinkedIn,
  selectHasCurrentUserLinkedAirbitz,
  (linkedFacebook, linkedGoogle, linkedLinkedIn, linkedAirbiz) => {
    return [linkedFacebook, linkedGoogle, linkedLinkedIn, linkedAirbiz].filter(val => val).length;
  }
);

export const selectImpressApiDetails = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.impressApi
);

export const selectIsSettingCurrentUserEthereumAddress = createSelector(
  selectAuthFeatureState,
  authFeatureState => authFeatureState.settingCurrentUserEthereumAddress
);

export const selectIsCurrentUserEthereumAddressVerified = createSelector(
  selectAuthFeatureState,
  selectHasCurrentUserLinkedAirbitz, // TODO: Remove when script is run on BE
  (authFeatureState, hasAirbitzLink) =>
    authFeatureState.currentUser
      ? authFeatureState.currentUser.ethereumAddressVerified || hasAirbitzLink
      : undefined
);

export const selectCanCurrentUserChangeEthereumAddress = createSelector(
  selectCurrentUserEthereumAddress,
  selectHasCurrentUserLinkedAirbitz,
  selectIsPollingAnyAdvisory,
  selectIsPollingAnyTeamMembership,
  (currentUserEthereumAddress, isAirbitzLinked, pollingAnyAdvisory, pollingAnyTeamMembership) => {
    const result =
      !!currentUserEthereumAddress &&
      !isAirbitzLinked &&
      !pollingAnyAdvisory &&
      !pollingAnyTeamMembership;
    return result;
  }
);

export const selectCurrentUserTcAndPrivacyConsents = createSelector(
  selectCurrentUser,
  currentUser => {
    return (currentUser && currentUser.termsPrivacy) || [];
  }
);

export const selectCurrentUserLatestTcAndPrivacyConsent = createSelector(
  selectCurrentUserTcAndPrivacyConsents,
  consents => {
    let mutableConsents = consents.asMutable ? consents.asMutable() : consents;
    mutableConsents = mutableConsents.sort((left, right) => {
      if (left.timestamp >= right.timestamp) return -1;
      return 1;
    });
    return mutableConsents[0];
  }
);

export const selectLatestAcceptedTcVersionByCurrentUser = createSelector(
  selectCurrentUserLatestTcAndPrivacyConsent,
  consent => {
    return consent ? Number(consent.tcVersion) : undefined;
  }
);

export const selectLatestAcceptedPrivacyVersionByCurrentUser = createSelector(
  selectCurrentUserLatestTcAndPrivacyConsent,
  consent => (consent ? Number(consent.privacyVersion) : undefined)
);

export const selectLatestAcceptedTcAndPrivacyByCurrentUserAt = createSelector(
  selectCurrentUserLatestTcAndPrivacyConsent,
  consent => (consent ? consent.timestamp : undefined)
);

export const selectHasCurrentUserAcceptedCurrentTermsAndPrivacy = createSelector(
  selectLatestAcceptedTcVersionByCurrentUser,
  selectLatestAcceptedPrivacyVersionByCurrentUser,
  (lastAcceptedTcVersionByCurrentUser, lastAcceptedPrivacyVersionByCurrentUser) => {
    return (
      (lastAcceptedTcVersionByCurrentUser &&
        lastAcceptedTcVersionByCurrentUser >= brand.termsOfUse.currentVersion.version) ||
      (lastAcceptedPrivacyVersionByCurrentUser &&
        lastAcceptedPrivacyVersionByCurrentUser >= brand.privacyPolicy.currentVersion.version)
    );
  }
);

export const selectCurrentUserDeletionRequests = createSelector(
  selectCurrentUser,
  currentUser =>
    currentUser.dataRequest && currentUser.dataRequest.filter(data => data.requestType === 'delete')
);

export const selectCurrentUserDeletionRequestedAt = createSelector(
  selectCurrentUserDeletionRequests,
  data => data && data.length && data[data.length - 1].timestamp
);

function isSoftRejected(advisory) {
  return (
    advisory.softConnection &&
    advisory.softConnection.status === 'REJECTED' &&
    advisory.softConnection.statusValidatedByUserAt
  );
}

export const selectUserAdvisoriesVisibleToCurrentUser = createSelector(
  selectUserAdvisories,
  (state, props) => props.userId,
  selectCurrentUserId,
  (advisories, userId, currentUserId) => {
    if (userId && userId === currentUserId) {
      return advisories.filter(
        ac =>
          ac.status !== ADVISOR_CONNECTION_STATUSES.rejectedByUser &&
          !isSoftRejected(ac) &&
          (ac.verificationTimestamp ? ac.status === ADVISOR_CONNECTION_STATUSES.signedByBoth : true)
      );
    } else {
      return advisories.filter(
        ac =>
          ac.status === ADVISOR_CONNECTION_STATUSES.signedByBoth ||
          ac.status === ADVISOR_CONNECTION_STATUSES.softStatusAcceptedByBoth
      );
    }
  }
);

export const selectUserTeamMembershipsVisibleToCurrentUser = createSelector(
  selectUserTeamMemberships,
  (state, props) => props.userId,
  selectCurrentUserId,
  (teamMemberships, userId, currentUserId) => {
    if (userId && userId === currentUserId) {
      return teamMemberships.filter(
        ac =>
          ac.status !== TEAM_MEMBER_CONNECTION_STATUSES.rejectedByUser &&
          (ac.verificationTimestamp
            ? ac.status === TEAM_MEMBER_CONNECTION_STATUSES.signedByBoth
            : true)
      );
    } else {
      return teamMemberships.filter(
        ac => ac.status === TEAM_MEMBER_CONNECTION_STATUSES.signedByBoth
      );
    }
  }
);

export const selectCurrentUsersJobRoles = createSelector(
  selectAuthFeatureState,
  authFeatureState => (authFeatureState.currentUser ? authFeatureState.currentUser.jobRoles : [])
);

export const selectCurrentUsersCurrentJobRoleId = createSelector(
  selectCurrentUsersJobRoles,
  jobRoles => (jobRoles && jobRoles.length ? jobRoles[0] : undefined)
);



// WEBPACK FOOTER //
// ./src/store/features/auth/selectors.js